
<template>
  <div class="main-content">
    <Container
      :title="t('route.paperColorDef')"
      :hasLine="true"
    >
      <template #header>
        <SearchForm
          ref="searchFormRef"
          :formModel="searchFormModel"
          :formStruct="searchFormStruct"
          :submitButton="t('common.search')"
          :resetButton="t('common.reset')"
          @submit="submitSearch"
          @reset="resetForm"
        />
      </template>
      <template #main>
        <el-button
          v-if="this.$hasPermission(['ACTION_vcPaperColorDefEdit'])"
          @click="handleCreate()"
          type="success"
        >
          {{ t('table.add') }}
        </el-button>
        <el-button
          v-if="selection.length > 0 && this.$hasPermission(['ACTION_vcPaperColorDefEdit'])"
          @click="handleBathDelete()"
          type="danger"
        >
          {{ t('table.bathDelete') }}
        </el-button>
        <SimpleTable
          :isLoading="listLoading"
          :tableData="tableData"
          :columnStruct="columnStruct"
          :sort="sort"
          :order="order"
          @handleOrder="handleOrder"
          @handleSelection="handleSelection"
        >
          <template
            v-if="this.$hasPermission(['ACTION_vcPaperColorDefEdit'])"
            #default="slotProps"
          >
            <el-button
              @click="handleEdit(slotProps.row)"
              type="primary"
              size="mini"
            >
              {{ t('common.edit') }}
            </el-button>
            <el-button
              @click="handleDelete(slotProps.row.id)"
              type="danger"
              size="mini"
            >
              {{ t('common.delete') }}
            </el-button>
          </template>
        </SimpleTable>
        <el-pagination
          :total="total"
          v-show="total > 0"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :page-size="limit"
          :current-page="page"
          :page-sizes="[5, 10, 20]"
          layout="total, sizes, prev, pager, next, jumper"
          style="text-align: right;"
        />
      </template>
    </Container>
    <el-dialog
      v-if="this.$hasPermission(['ACTION_vcPaperColorDefEdit'])"
      :title="t(textMap[dialogStatus])"
      center
      v-model="dialogFormVisible"
      width="45%"
    >
      <NomalContainer>
        <CenterForm
          v-if="dialogStatus === 'create'"
          :formModel="createModel"
          :formStruct="createStruct"
          :formRules="paperColorDefRole"
          @submit="createSubmit"
          @reset="createReset"
        />
        <CenterForm
          v-if="dialogStatus === 'update'"
          :formModel="updateModel"
          :formStruct="updateStruct"
          :formRules="paperColorDefRole"
          @submit="updateNameSubmit"
          @reset="updateNameReset"
        />
      </NomalContainer>
    </el-dialog>
  </div>
</template>

<script lang="ts">
import { ElForm, ElMessage, ElMessageBox } from 'element-plus'
import { defineComponent, reactive, toRefs, onMounted, ref } from 'vue'
import Container from '@/components/form/container/Container.vue'
import SearchForm from '@/components/form/search/SearchForm.vue'
import SimpleTable from '@/components/table/simple/SimpleTable.vue'
import CenterForm from '@/components/form/common/CenterForm.vue'
import NomalContainer from '@/components/dfms-components/container/NomalContainer.vue'
import {
  PaperColorDefModel,
  PaperColorDefPageSearchModel,
  PaperColorDefSearchModel,
  PaperColorDefAddModel,
  PaperColorDefUpdateModel,
  IdsModel
} from '@/model/product/PaperColorDefModel'
import {
  pageSearchPaperColorDef,
  addPaperColorDef,
  updatePaperColorDef,
  removePaperColorDef,
  batchRemovePaperColorDef
} from '@/apis/product/paperColorDef'
import { parseTime } from '@/utils'
import { createModel } from '@/utils/component'
import { useI18n } from 'vue-i18n'

export default defineComponent({
  components: {
    Container,
    SearchForm,
    SimpleTable,
    CenterForm,
    NomalContainer
  },
  setup() {
    const { t } = useI18n()
    const searchFormRef = ref(ElForm)
    const searchStruct = [
      { prop: 'colorComment', label: t('paperColorDef.colorComment'), placeholder: t('paperColorDef.colorCommentP'), component: 'el-input', type: 'text', model: null },
      { prop: 'colorValueR', label: t('paperColorDef.colorValueR'), placeholder: t('paperColorDef.colorValueRP'), component: 'el-input', type: 'number', model: null },
      { prop: 'colorValueG', label: t('paperColorDef.colorValueG'), placeholder: t('paperColorDef.colorValueGP'), component: 'el-input', type: 'number', model: null },
      { prop: 'colorValueB', label: t('paperColorDef.colorValueB'), placeholder: t('paperColorDef.colorValueBP'), component: 'el-input', type: 'number', model: null }
    ]

    const createStruct = [
      { prop: 'colorComment', label: t('paperColorDef.colorComment'), placeholder: t('paperColorDef.colorCommentP'), component: 'el-input', type: 'text', model: null },
      { prop: 'colorValueR', label: t('paperColorDef.colorValueR'), placeholder: t('paperColorDef.colorValueRP'), component: 'el-input', type: 'number', model: null },
      { prop: 'colorValueG', label: t('paperColorDef.colorValueG'), placeholder: t('paperColorDef.colorValueGP'), component: 'el-input', type: 'number', model: null },
      { prop: 'colorValueB', label: t('paperColorDef.colorValueB'), placeholder: t('paperColorDef.colorValueBP'), component: 'el-input', type: 'number', model: null }
    ]

    const updateStruct = [
      { prop: 'colorComment', label: t('paperColorDef.colorComment'), placeholder: t('paperColorDef.colorCommentP'), component: 'el-input', type: 'text', model: null },
      { prop: 'colorValueR', label: t('paperColorDef.colorValueR'), placeholder: t('paperColorDef.colorValueRP'), component: 'el-input', type: 'number', model: null },
      { prop: 'colorValueG', label: t('paperColorDef.colorValueG'), placeholder: t('paperColorDef.colorValueGP'), component: 'el-input', type: 'number', model: null },
      { prop: 'colorValueB', label: t('paperColorDef.colorValueB'), placeholder: t('paperColorDef.colorValueBP'), component: 'el-input', type: 'number', model: null }
    ]

    const paperColorDefRole = {
      colorComment: [{ required: true, message: t('paperColorDef.colorComment').concat(t('common.isRequired')), trigger: 'blur' }],
      colorValueR: [{ required: true, message: t('paperColorDef.colorValueR').concat(t('common.isRequired')), trigger: 'blur' }],
      colorValueG: [{ required: true, message: t('paperColorDef.colorValueG').concat(t('common.isRequired')), trigger: 'blur' }],
      colorValueB: [{ required: true, message: t('paperColorDef.colorValueB').concat(t('common.isRequired')), trigger: 'blur' }]
    }

    const tableStruct = [

      { prop: 'colorComment', label: t('paperColorDef.colorComment'), sortable: 'custom' },
      { prop: 'colorValueR', label: t('paperColorDef.colorValueR'), sortable: 'custom' },
      { prop: 'colorValueG', label: t('paperColorDef.colorValueG'), sortable: 'custom' },
      { prop: 'colorValueB', label: t('paperColorDef.colorValueB'), sortable: 'custom' },
      { prop: 'createTime', label: t('common.createTime'), formatter: (row: PaperColorDefModel) => { return parseTime(row.createTime) }, sortable: 'custom' },
      { prop: 'updateTime', label: t('common.updateTime'), formatter: (row: PaperColorDefModel) => { return parseTime(row.updateTime) }, sortable: 'custom' }
    ]
    const dataMap = reactive({
      searchFormStruct: searchStruct,
      searchFormModel: createModel(searchStruct) as PaperColorDefSearchModel,
      tableData: Array<PaperColorDefModel>(),
      columnStruct: tableStruct,
      listLoading: true,
      selection: Array<number>(),
      limit: 10,
      total: 0,
      page: 1,
      sort: 'createTime',
      order: 'desc',
      dialogStatus: 'create',
      textMap: {
        update: 'paperColorDef.edit',
        create: 'paperColorDef.create'
      },
      dialogFormVisible: false,
      paperColorDefRole: paperColorDefRole,
      createStruct: createStruct,
      createModel: createModel(createStruct) as PaperColorDefAddModel,
      updateStruct: updateStruct,
      updateModel: createModel(updateStruct) as PaperColorDefUpdateModel,
      editRow: {} as PaperColorDefModel,
      handleSelection(item: any[]) {
        dataMap.selection = item
      },
      handleCurrentChange(page?: any) {
        dataMap.page = page
        dataMap.getList()
      },
      handleSizeChange(val: any) {
        dataMap.limit = val
        dataMap.getList()
      },
      handleOrder(column: any) {
        dataMap.sort = column.prop
        dataMap.order = column.order === 'descending' ? 'desc' : 'asc'
        dataMap.getList()
      },
      async getList() {
        dataMap.listLoading = true
        const params: PaperColorDefPageSearchModel = {
          colorComment: dataMap.searchFormModel.colorComment,
          colorValueR: dataMap.searchFormModel.colorValueR,
          colorValueG: dataMap.searchFormModel.colorValueG,
          colorValueB: dataMap.searchFormModel.colorValueB,
          page: dataMap.page,
          limit: dataMap.limit,
          sort: dataMap.sort,
          order: dataMap.order
        }
        const data = await pageSearchPaperColorDef(params)
        dataMap.tableData = data?.data?.records ?? []
        const total = data?.data?.total ?? '0'
        dataMap.total = parseInt(total)

        // Just to simulate the time of the request
        setTimeout(() => {
          dataMap.listLoading = false
        }, 0.5 * 1000)
      },
      submitSearch() {
        dataMap.page = 1
        dataMap.getList()
      },
      resetForm() {
        console.log('reset')
      },
      async createSubmit() {
        const createData: PaperColorDefAddModel = {
          colorComment: dataMap.createModel.colorComment,
          colorValueR: dataMap.createModel.colorValueR,
          colorValueG: dataMap.createModel.colorValueG,
          colorValueB: dataMap.createModel.colorValueB
        }
        const result = await addPaperColorDef(createData)
        dataMap.dialogFormVisible = false
        dataMap.getList()
        if (result?.code === 0) {
          ElMessage({
            message: t('common.createSuccess'),
            type: 'success',
            duration: 2000
          })
        } else {
          ElMessage({
            message: result?.message,
            type: 'error',
            duration: 2000
          })
        }
      },
      async updateNameSubmit() {
        const updateData: PaperColorDefUpdateModel = {
          id: dataMap.updateModel.id,
          colorComment: dataMap.updateModel.colorComment,
          colorValueR: dataMap.updateModel.colorValueR,
          colorValueG: dataMap.updateModel.colorValueG,
          colorValueB: dataMap.updateModel.colorValueB
        }
        const result = await updatePaperColorDef(updateData)
        dataMap.dialogFormVisible = false
        dataMap.getList()
        if (result?.code === 0) {
          ElMessage({
            message: t('common.updateSuccess'),
            type: 'success',
            duration: 2000
          })
        } else {
          ElMessage({
            message: result?.message,
            type: 'error',
            duration: 2000
          })
        }
      },
      async deleteData(id: number) {
        const result = await removePaperColorDef(id)
        dataMap.getList()
        if (result?.code === 0) {
          ElMessage({
            message: t('common.deleteSuccess'),
            type: 'success',
            duration: 2000
          })
        } else {
          ElMessage({
            message: result?.message,
            type: 'error',
            duration: 2000
          })
        }
      },
      async bathDeleteData() {
        const deleteData: IdsModel = { ids: dataMap.selection }
        const result = await batchRemovePaperColorDef(deleteData)
        dataMap.getList()
        if (result?.code === 0) {
          ElMessage({
            message: t('common.deleteSuccess'),
            type: 'success',
            duration: 2000
          })
        } else {
          ElMessage({
            message: result?.message,
            type: 'error',
            duration: 2000
          })
        }
      },
      updateNameReset() {
        console.log('update reset')
        dataMap.updateModel.id = dataMap.editRow.id
        dataMap.updateModel.colorComment = dataMap.editRow.colorComment
        dataMap.updateModel.colorValueR = dataMap.editRow.colorValueR
        dataMap.updateModel.colorValueG = dataMap.editRow.colorValueG
        dataMap.updateModel.colorValueB = dataMap.editRow.colorValueB
      },
      createReset() {
        console.log('create reset')
      }
    })
    const handleCreate = () => {
      dataMap.dialogStatus = 'create'
      dataMap.dialogFormVisible = true
    }
    const handleEdit = (row?: PaperColorDefModel) => {
      if (row) {
        dataMap.editRow = row
        dataMap.dialogStatus = 'update'
        dataMap.updateModel.id = row.id
        dataMap.updateModel.colorComment = row.colorComment
        dataMap.updateModel.colorValueR = row.colorValueR
        dataMap.updateModel.colorValueG = row.colorValueG
        dataMap.updateModel.colorValueB = row.colorValueB
        dataMap.dialogFormVisible = true
      }
    }
    const handleDelete = (id: number) => {
      ElMessageBox.confirm(t('common.deleteTip'), t('message.tip'), {
        confirmButtonText: t('message.confirm'),
        cancelButtonText: t('message.cancel'),
        type: 'warning'
      }).then(async() => {
        await dataMap.deleteData(id)
      }).catch(() => {
        ElMessage({
          type: 'info',
          message: t('common.cancelDelete'),
          duration: 2000
        })
      })
    }
    const handleBathDelete = () => {
      ElMessageBox.confirm(t('common.deleteTip'), t('message.tip'), {
        confirmButtonText: t('message.confirm'),
        cancelButtonText: t('message.cancel'),
        type: 'warning'
      }).then(async() => {
        await dataMap.bathDeleteData()
      }).catch(() => {
        ElMessage({
          type: 'info',
          message: t('common.cancelDelete'),
          duration: 2000
        })
      })
    }
    onMounted(() => {
      dataMap.getList()
    })
    return {
      t,
      handleCreate,
      handleEdit,
      handleDelete,
      handleBathDelete,
      searchFormRef,
      ...toRefs(dataMap)
    }
  }
})
</script>
